import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/alert-dialog/alert-dialog';

import type { BaseDialogProps } from '@/contexts/dialog-context/v2-base-dialog-props';
import { useV2DialogContext } from '@/contexts/dialog-context/v2-dialog-context';
import { deleteUserMutationOptions } from '@/features/users/api/delete-user.api';
import { GET_LIST_USER_QUERY_KEY } from '@/features/users/api/get-list-user.api';

export interface DeleteUserDialogProps extends BaseDialogProps {
  userId: string;
}

export default function DeleteUserDialog({
  dialog,
  userId,
}: DeleteUserDialogProps) {
  const { closeDeleteUserDialog } = useV2DialogContext();
  const queryClient = useQueryClient();

  const deleteUserMutation = useMutation({
    ...deleteUserMutationOptions(),
    onSuccess: () => {
      toast.success('Pengguna berhasil dihapus');
      queryClient.invalidateQueries({ queryKey: [GET_LIST_USER_QUERY_KEY] });
      closeDeleteUserDialog();
    },
    onError: error => {
      const message =
        error instanceof AxiosError
          ? ((error.response?.data as { message?: string })?.message ??
            error.message)
          : 'Gagal menghapus pengguna';
      toast.error(message);
    },
  });

  const isPending = deleteUserMutation.isPending;

  return (
    <AlertDialog
      {...dialog}
      onOpenChange={nextOpenState => {
        if (!nextOpenState && !isPending) {
          closeDeleteUserDialog();
        }
      }}
    >
      <AlertDialogContent showCloseButton={false}>
        <AlertDialogHeader>
          <AlertDialogTitle>Hapus pengguna ini?</AlertDialogTitle>
          <AlertDialogDescription>
            Tindakan ini tidak dapat dibatalkan. Data pengguna akan dihapus
            secara permanen dari sistem.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Batal</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={isPending}
            onClick={() => deleteUserMutation.mutate({ user_id: userId })}
          >
            {isPending ? 'Menghapus...' : 'Hapus'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
